import { memo, useMemo } from 'react';
import { DELIVERY_TYPE_COLORS } from '../shared/constants';
import { usePartnershipData } from './usePartnershipData.jsx';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';

export const PartnerFlowChart = memo(function PartnerFlowChart({ partnerData: dataProp }) {
  const { partnerMap, localFilteredResults } = usePartnershipData();
  const partnerData = dataProp ?? partnerMap;

  if (!partnerData || partnerData.size === 0) return (
    <ChartWrapper title="Partner Flow">
      <NoChartData message="No partner flow data available" detail="This chart visualizes partner relationships by delivery type (Innovation, Scaling, Demand). No partner data with delivery type information was found." />
    </ChartWrapper>
  );

  // Prepare chart data for download
  const chartData = useMemo(() => {
    const data = [];
    partnerData.forEach(partner => {
      const types = partner.deliveryTypes && partner.deliveryTypes.length > 0 ? partner.deliveryTypes : ['Other'];
      types.forEach(dt => {
        data.push({
          partner_acronym: partner.acronym,
          partner_name: partner.fullName,
          delivery_type: dt,
          result_count: partner.resultCount
        });
      });
    });
    return data;
  }, [partnerData]);

  const byDeliveryType = {};
  partnerData.forEach(partner => {
    const types = partner.deliveryTypes && partner.deliveryTypes.length > 0 ? partner.deliveryTypes : ['Other'];
    types.forEach(dt => {
      byDeliveryType[dt] = (byDeliveryType[dt] || 0) + partner.resultCount;
    });
  });

  const typeData = Object.entries(byDeliveryType)
    .map(([name, value]) => ({ name, value, fill: DELIVERY_TYPE_COLORS[name] || DELIVERY_TYPE_COLORS['Other'] }))
    .sort((a, b) => b.value - a.value);

  const totalPartners = partnerData.size;
  const totalResults = typeData.reduce((s, d) => s + d.value, 0);

  return (
    <ChartWrapper title="Partner Network Overview" info="Distribution of partner–result engagements across delivery types. Each link represents one partner's involvement in one result. Partners contributing to multiple results are counted multiple times." chartData={chartData} sourceResults={localFilteredResults} exportFilename="partner-network" description="Counts partner–result links by delivery role. One partner contributing to 5 results counts as 5 links. See Partnership Landscape for unique partner counts.">
      <p className="text-sm text-gray-500 -mt-1 mb-3">Each bar shows the total number of results involving partners in each delivery role</p>

      {/* Summary metrics */}
      <div className="flex gap-10 mb-5">
        <div>
          <div className="text-2xl font-bold text-gray-900">{totalPartners.toLocaleString()}</div>
          <div className="text-xs text-gray-500">Partners</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{totalResults.toLocaleString()}</div>
          <div className="text-xs text-gray-500">Partner–Result Links</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{typeData.length}</div>
          <div className="text-xs text-gray-500">Delivery Types</div>
        </div>
      </div>

      {/* Delivery type bars */}
      <div className="space-y-2">
        {typeData.map(item => {
          const pct = totalResults > 0 ? Math.round((item.value / totalResults) * 100) : 0;
          return (
            <div key={item.name} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.fill }} />
              <div className="w-28 sm:w-40 text-sm text-gray-600 truncate" title={item.name}>{item.name}</div>
              <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden relative">
                {[10,20,30,40,50,60,70,80,90].map(tick => (
                  <div key={tick} className="absolute top-0 bottom-0 w-px bg-gray-300/40" style={{ left: `${tick}%` }} />
                ))}
                <div className="h-full rounded-full transition-all relative" style={{ width: `${pct}%`, backgroundColor: item.fill }} />
              </div>
              <div className="w-20 text-xs font-medium text-gray-900 text-right">{item.value} ({pct}%)</div>
            </div>
          );
        })}
      </div>

      {/* Distribution bar */}
      {typeData.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-100">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Distribution by Delivery Type</h5>
          <div className="h-6 w-full rounded-full overflow-hidden flex">
            {typeData.map(item => {
              const pct = totalResults > 0 ? (item.value / totalResults) * 100 : 0;
              return (
                <div key={item.name} className="h-full transition-all flex items-center justify-center overflow-visible" style={{ width: `${pct}%`, backgroundColor: item.fill }} title={`${item.name}: ${item.value} results (${Math.round(pct)}%)`}>
                  {pct >= 8 && <span className="text-[10px] font-bold text-white whitespace-nowrap">{Math.round(pct)}%</span>}
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-2">
            {typeData.map(item => (
              <div key={item.name} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.fill }} />
                <span className="text-xs text-gray-500">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </ChartWrapper>
  );
});
